import showAlert from "../server/ShowAlert";
import * as actionType from "./action";

const initialState = {
  productsInCart: [],
  productSale: [],
  orderSum: 0,
  totalSum: 0,
  orderTime: null,
  refresh: false,
  deliveryPrice: null,
  deliveryAvailable: false,
  minOrder: 250,
  // Map of productId → snapshot of the cart item BEFORE optimistic update.
  // null means the item was not in the cart before (optimistic add).
  pendingUpdates: {},
};

// ── Helpers ──

const calculateSum = (cartItems) => {
  const validProducts = cartItems.filter(
    (p) => p.totalPrice != null && p.quantity != null,
  );
  let orderSum = (
    parseFloat(validProducts.reduce((prev, x) => prev + +x.totalPrice, 0)) || 0
  ).toFixed(2);
  return { orderSum };
};

const handleUpsertProduct = (state, payload) => {
  const { data, productId, promoId } = payload;
  let productsInCart = [...state.productsInCart];
  let refresh = false;

  if (data.status === 409 || data.status === "409") {
    let msg = "המוצר לא במלאי";
    const productInCart = productsInCart.find(
      (product) => product.productId === productId,
    );
    if (productInCart) {
      msg = `מצטערים, יש במלאי ${productInCart.quantity} יחידות `;
      const index = productsInCart.findIndex(
        (product) => product.productId === productId,
      );
      productsInCart[index] = {
        ...productInCart,
        warningMessage: msg,
      };
    }
    showAlert({ msgTitle: msg, msgTimer: 3000 });
    refresh = true;
    return { productsInCart, refresh };
  }

  if (data.status === "deleted") {
    productsInCart = productsInCart.filter(
      (product) => product.productId !== productId,
    );
    return { productsInCart, refresh };
  }

  // Normal upsert from server response
  const index = productsInCart.findIndex(
    (product) => product.productId === data.reservedItem.productId,
  );

  let warningMessage = "";
  if (data.promoUsageLimited) {
    warningMessage =
      "כמות המימושים המקסימלית לקניה למבצע כבר נאספה, כעת יתווסף המוצר במחירו הרגיל";
  }

  if (data.reservedItem.quantity === 0) {
    if (index >= 0) {
      productsInCart.splice(index, 1);
    }
  } else if (index >= 0) {
    productsInCart[index] = {
      cartId: data.cartId,
      productId: data.reservedItem.productId,
      product: data.reservedItem.product,
      quantity: data.reservedItem.quantity,
      totalPrice: data.reservedItem.totalPrice,
      unitPrice: data.reservedItem.unitPrice,
      promoId: data.reservedItem.promoId,
    };
    if (warningMessage) {
      productsInCart[index].warningMessage = warningMessage;
    }
  } else {
    const newItem = {
      cartId: data.cartId,
      productId: data.reservedItem.productId,
      product: data.reservedItem.product,
      quantity: data.reservedItem.quantity,
      totalPrice: data.reservedItem.totalPrice,
      unitPrice: data.reservedItem.unitPrice,
      promoId: data.reservedItem.promoId,
      warningMessage: warningMessage ?? "",
    };
    productsInCart.push(newItem);
  }

  if (data.reservedItem.promoId && !promoId) {
    showAlert({ msgTitle: "מבצע חדש", msgTimer: 3000 });
  }
  if (data.reservedItem.isPriceChanged) {
    showAlert({
      msgTitle: `המחיר למוצר השתנה כעת ל ${parseFloat(
        data.reservedItem.priceChangedItem.unitPrice,
      ).toFixed(2)}`,
      msgTimer: 3000,
    });
  }

  return { productsInCart, refresh };
};

const handleRevalidationItems = (productsInCart, data) => {
  let msg = "";
  productsInCart = productsInCart.map((product) => {
    const revalidatedItem = data.revalidatedItems[product.productId];
    if (!revalidatedItem) return product;
    if (revalidatedItem.isPriceChanged) {
      msg += `המחיר למוצר ${product.product.name} השתנה ל ${revalidatedItem.totalPrice}`;
      if (!data.promoUsageLimited && product.warningMessage) {
        product.warningMessage = "";
      }
      return {
        ...product,
        totalPrice: revalidatedItem.totalPrice,
        unitPrice: revalidatedItem.unitPrice,
      };
    }
    return product;
  });
  if (msg) {
    showAlert({ msgTitle: "שים לב", msgText: msg, msgTimer: 3000 });
  }
  return productsInCart;
};

// ── Reducer ──

const reducer = (state = initialState, action) => {
  switch (action.type) {
    // ─── OPTIMISTIC: instant UI update on +/- click ───
    case actionType.OPTIMISTIC_CART_UPDATE: {
      const { productId, quantity, unitPrice, promoId, product } =
        action.payload;
      let productsInCart = [...state.productsInCart];
      const pendingUpdates = { ...state.pendingUpdates };
      const index = productsInCart.findIndex((p) => p.productId === productId);

      // Save snapshot for rollback (only the FIRST optimistic update per product;
      // subsequent clicks before confirm should not overwrite the original snapshot).
      if (!(productId in pendingUpdates)) {
        pendingUpdates[productId] =
          index >= 0
            ? { ...productsInCart[index] } // deep-enough copy of current item
            : null; // item was not in cart
      }

      if (quantity <= 0) {
        // ── Remove from cart (optimistic) ──
        if (index >= 0) {
          productsInCart.splice(index, 1);
        }
      } else if (index >= 0) {
        // ── Update existing item (optimistic) ──
        productsInCart[index] = {
          ...productsInCart[index],
          quantity,
          // Estimate totalPrice; server will correct on CONFIRM.
          // Keep the existing totalPrice ratio if promo exists for a less jarring estimate.
          totalPrice: productsInCart[index].promoId
            ? productsInCart[index].totalPrice +
              (quantity - productsInCart[index].quantity) * unitPrice
            : unitPrice * quantity,
          warningMessage: "", // clear any previous warning
        };
        // Ensure totalPrice doesn't go below 0 due to promo estimate
        if (productsInCart[index].totalPrice < 0) {
          productsInCart[index].totalPrice = 0;
        }
      } else {
        // ── Add new item to cart (optimistic) ──
        productsInCart.push({
          cartId: "pending",
          productId,
          product: product || null,
          quantity,
          totalPrice: unitPrice * quantity,
          unitPrice,
          promoId: promoId || null,
          warningMessage: "",
        });
      }

      const sum = calculateSum(productsInCart);
      return {
        ...state,
        productsInCart,
        pendingUpdates,
        orderSum: sum.orderSum,
      };
    }

    // ─── CONFIRM: server responded OK → apply real data ───
    case actionType.CONFIRM_CART_UPDATE: {
      const { productId } = action.payload;
      const data = handleUpsertProduct(state, action.payload);
      const sum = calculateSum(data.productsInCart || []);

      // Clear the pending snapshot for this product
      const pendingUpdates = { ...state.pendingUpdates };
      delete pendingUpdates[productId];

      return {
        ...state,
        productsInCart: [...data.productsInCart],
        pendingUpdates,
        orderSum: sum.orderSum,
        deliveryPrice: state.deliveryPrice,
        deliveryAvailable: state.deliveryAvailable,
        refresh: true,
      };
    }

    // ─── ROLLBACK: server error → revert to last confirmed state ───
    case actionType.ROLLBACK_CART_UPDATE: {
      const { productId, serverResponse } = action.payload;
      let productsInCart = [...state.productsInCart];
      const pendingUpdates = { ...state.pendingUpdates };
      const snapshot = pendingUpdates[productId];

      if (snapshot === undefined) {
        // No snapshot – nothing to rollback (shouldn't happen, but be safe)
        return state;
      }

      const currentIndex = productsInCart.findIndex(
        (p) => p.productId === productId,
      );

      if (snapshot === null) {
        // Item was NOT in cart before optimistic add → remove it
        if (currentIndex >= 0) {
          productsInCart.splice(currentIndex, 1);
        }
      } else {
        // Restore the saved snapshot
        if (currentIndex >= 0) {
          productsInCart[currentIndex] = { ...snapshot };
        } else {
          // Item was optimistically removed but server says it should exist → re-add
          productsInCart.push({ ...snapshot });
        }
      }

      delete pendingUpdates[productId];

      // Show appropriate error message
      if (
        serverResponse &&
        (serverResponse.status === 409 || serverResponse.status === "409")
      ) {
        const restoredItem = productsInCart.find(
          (p) => p.productId === productId,
        );
        const msg = restoredItem
          ? `מצטערים, יש במלאי ${restoredItem.quantity} יחידות`
          : "המוצר לא במלאי";
        showAlert({ msgTitle: msg, msgTimer: 3000 });

        // Add warning message to the item
        if (restoredItem) {
          const idx = productsInCart.findIndex(
            (p) => p.productId === productId,
          );
          productsInCart[idx] = { ...restoredItem, warningMessage: msg };
        }
      } else if (!serverResponse) {
        showAlert({ msgTitle: "שגיאה בעדכון העגלה, נסה שנית", msgTimer: 3000 });
      }

      const sum = calculateSum(productsInCart);
      return {
        ...state,
        productsInCart,
        pendingUpdates,
        orderSum: sum.orderSum,
        refresh: true,
      };
    }

    // ─── Existing actions (unchanged) ───

    case actionType.ADD_PRODUCT_IN_CART: {
      const data = handleUpsertProduct(state, action.payload);
      const sum = calculateSum(data.productsInCart || []);
      return {
        ...state,
        productsInCart: [...data.productsInCart],
        orderSum: sum.orderSum,
        deliveryPrice: state.deliveryPrice,
        deliveryAvailable: state.deliveryAvailable,
        refresh: true,
      };
    }

    case actionType.SET_REVALIDATED_PRODUCTS: {
      const data = handleUpsertProduct(state, action.payload);
      const sum = calculateSum(data.productsInCart || []);
      const productsInCart = handleRevalidationItems(
        data.productsInCart,
        action.payload.data,
      );
      // Clear any pending snapshot for the revalidated product
      const pendingUpdates = { ...state.pendingUpdates };
      delete pendingUpdates[action.payload.productId];
      return {
        ...state,
        productsInCart,
        pendingUpdates,
        orderSum: sum.orderSum,
        deliveryPrice: state.deliveryPrice,
        deliveryAvailable: state.deliveryAvailable,
        refresh: true,
      };
    }

    case actionType.SET_ALL_PRODUCT: {
      const sumSet = calculateSum(action.payload.cart.items || []);
      const outOfStockItems = action.payload.missingQuantities || null;
      if (outOfStockItems) {
        const msg =
          "שימו 💥 לב: חלק מהמוצרים אזלו מהמלאי או שהכמויות השתנו, כיוון שהעגלה נשארה פתוחה זמן רב. מומלץ לבדוק מחדש את העגלה לפני השלמת ההזמנה.";
        showAlert({ msgTitle: msg, msgTimer: 3000 });
      }
      return {
        ...state,
        orderSum: sumSet.orderSum,
        productsInCart: action.payload.cart.items || [],
        pendingUpdates: {}, // clear all pending on full refresh
        deliveryPrice: state.deliveryPrice,
        deliveryAvailable: state.deliveryAvailable,
      };
    }

    case actionType.CLEAR_CARD:
      return {
        ...state,
        productsInCart: [],
        productSale: [],
        totalSum: 0,
        orderSum: 0,
        orderTime: null,
        pendingUpdates: {},
      };

    case actionType.CANCEL_ERROR:
      return { ...state };

    case actionType.SET_ORDER_TIME:
      return { ...state, orderTime: action.payload };

    case actionType.SET_PRODUCTS:
      return { ...state, refresh: false };

    case actionType.SET_DELIVERY_PRICE:
      return action.payload.deliveryAvailable
        ? {
            ...state,
            deliveryPrice: action.payload.deliveryPrice,
            deliveryAvailable: true,
          }
        : {
            ...state,
            deliveryPrice: action.payload.deliveryPrice,
            deliveryAvailable: false,
          };

    case actionType.SET_MIN_ORDER:
      return { ...state, minOrder: action.payload };

    case actionType.SET_ORDER_FOR_FREE:
      return { ...state, orderForFree: action.payload };

    case actionType.LOGOUT: {
      console.log("logout in cart");
      return {
        ...initialState,
      };
    }

    default:
      return state;
  }
};

export default reducer;
